import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { api } from '../api/client.js';
import { useTenant } from '../context/TenantContext.jsx';
import { formatBrl } from '../utils/currency.js';
import {
  eachLocalDayInRange,
  isTimestampInRange,
  resolveDashboardRange,
  toLocalYmd,
} from '../utils/dashboardDateRange.js';
import { orderStatusLabelPtBr } from '../utils/orderStatus.js';

/** Máximo de empresas agregadas em modo “todas” (evita centenas de pedidos em super_admin). */
const MAX_CONSOLIDATED_TENANTS = 18;

const PRESETS = [
  { id: '7', label: '7 dias' },
  { id: '15', label: '15 dias' },
  { id: '30', label: '30 dias' },
  { id: 'custom', label: 'Período' },
];

const STATUS_ORDER = ['pending', 'paid', 'sent_to_erp', 'cancelled'];
const STATUS_STYLE = {
  pending: 'from-amber-400 to-amber-600',
  paid: 'from-emerald-400 to-accent',
  sent_to_erp: 'from-sky-400 to-sky-600',
  cancelled: 'from-zinc-400 to-zinc-600',
};

function formatRangeLabel(from, to) {
  const opts = { day: '2-digit', month: 'short', year: 'numeric' };
  return `${from.toLocaleDateString('pt-BR', opts)} — ${to.toLocaleDateString('pt-BR', opts)}`;
}

/**
 * @param {object} props
 * @param {string} props.title
 * @param {string} props.sub
 * @param {string} props.value
 * @param {string} [props.hint]
 * @param {string} [props.accent]
 */
function KpiTile({ title, sub, value, hint, accent = 'accent' }) {
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-ink/10 bg-white/75 p-5 shadow-md backdrop-blur-md transition duration-300 hover:-translate-y-0.5 hover:shadow-night-card-hover dark:border-white/[0.08] dark:bg-night-surface/90 dark:shadow-night-card">
      <div
        className={`pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br opacity-40 blur-2xl transition group-hover:opacity-70 ${
          accent === 'accent2' ? 'from-accent2 to-amber-400' : 'from-accent to-emerald-300'
        }`}
      />
      <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-ink/45 dark:text-frost-muted">{title}</p>
      <p className="mt-3 font-display text-2xl font-semibold tracking-tight text-ink dark:text-frost">{value}</p>
      <p className="mt-1 text-xs font-medium text-ink/55 dark:text-frost-muted">{sub}</p>
      {hint ? <p className="mt-2 text-[11px] leading-snug text-ink/40 dark:text-frost-muted">{hint}</p> : null}
    </div>
  );
}

/** Barras diárias simples (sem biblioteca de gráficos). */
function DailyBars({ days, byDay }) {
  const maxVal = Math.max(1, ...days.map((d) => byDay[toLocalYmd(d)]?.revenue || 0));
  return (
    <div className="flex h-44 items-end gap-1.5 px-1">
      {days.map((d) => {
        const key = toLocalYmd(d);
        const cell = byDay[key] || { count: 0, revenue: 0 };
        const h = Math.round((cell.revenue / maxVal) * 100);
        const label = d.toLocaleDateString('pt-BR', { weekday: 'narrow', day: 'numeric' });
        return (
          <div key={key} className="flex min-w-0 flex-1 flex-col items-center gap-1" title={`${label}: ${formatBrl(cell.revenue)} · ${cell.count} pedidos`}>
            <div className="flex w-full flex-1 items-end justify-center">
              <div
                className="w-full max-w-[14px] rounded-t-md bg-gradient-to-t from-accent/90 to-emerald-300/80 shadow-glow transition hover:brightness-110 dark:from-accent dark:to-emerald-200/70"
                style={{ height: `${Math.max(8, h)}%` }}
              />
            </div>
            <span className="truncate text-[9px] font-semibold uppercase tracking-tighter text-ink/40 dark:text-frost-muted">
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Donut CSS com conic-gradient. */
function StatusDonut({ counts }) {
  const total = STATUS_ORDER.reduce((s, k) => s + (counts[k] || 0), 0);
  if (total === 0) {
    return (
      <div className="flex h-40 items-center justify-center rounded-2xl border border-dashed border-ink/15 text-sm text-ink/45 dark:border-white/10 dark:text-frost-muted">
        Sem pedidos neste intervalo
      </div>
    );
  }
  const colors = {
    pending: '#f59e0b',
    paid: '#278f72',
    sent_to_erp: '#38bdf8',
    cancelled: '#71717a',
  };
  let acc = 0;
  const segments = STATUS_ORDER.map((k) => {
    const n = counts[k] || 0;
    const start = (acc / total) * 360;
    acc += n;
    const end = (acc / total) * 360;
    return { k, start, end, color: colors[k] };
  }).filter((s) => s.end > s.start);

  let gradient = '';
  segments.forEach((s) => {
    gradient += `${s.color} ${s.start}deg ${s.end}deg, `;
  });
  gradient = gradient.replace(/, $/, '');
  const style = { background: `conic-gradient(${gradient})` };

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
      <div className="relative h-36 w-36 shrink-0 rounded-full p-3 shadow-glow" style={style}>
        <div className="flex h-full w-full items-center justify-center rounded-full bg-white dark:bg-night-deep">
          <div className="text-center">
            <p className="font-display text-xl font-bold text-ink dark:text-frost">{total}</p>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-ink/45 dark:text-frost-muted">pedidos</p>
          </div>
        </div>
      </div>
      <ul className="flex flex-wrap justify-center gap-3 text-xs sm:flex-col sm:items-start">
        {STATUS_ORDER.map((k) => (
          <li key={k} className="flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full bg-gradient-to-br ${STATUS_STYLE[k]}`} />
            <span className="font-medium text-ink/75 dark:text-mist">{orderStatusLabelPtBr(k)}</span>
            <span className="tabular-nums text-ink/50 dark:text-frost-muted">{counts[k] || 0}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function DashboardPage() {
  const { user } = useOutletContext() || {};
  const { tenantId, setTenantId } = useTenant();
  const [preset, setPreset] = useState(/** @type {'7' | '15' | '30' | 'custom'} */ ('7'));
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  function applyPreset(id) {
    setPreset(id);
    if (id === 'custom' && !customFrom && !customTo) {
      const to = new Date();
      const from = new Date();
      from.setDate(from.getDate() - 6);
      setCustomFrom(toLocalYmd(from));
      setCustomTo(toLocalYmd(to));
    }
  }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  /** @type {[{ id: number; nome: string; slug: string }]} */
  const [tenantList, setTenantList] = useState([]);
  /** @type {Array<object>} */
  const [ordersAll, setOrdersAll] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);

  const range = useMemo(
    () => resolveDashboardRange(preset, customFrom, customTo),
    [preset, customFrom, customTo]
  );

  const rangeLabel = range ? formatRangeLabel(range.from, range.to) : 'Período inválido';

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const { tenants: rawTenants } = await api('/tenants');
      const tenants = rawTenants || [];
      const allowedIds = new Set(tenants.map((t) => Number(t.id)));
      let tid = tenantId != null && Number(tenantId) > 0 ? Number(tenantId) : null;
      if (tid != null && !allowedIds.has(tid)) {
        setTenantId(null);
        tid = null;
      }
      setTenantList(tenants.map((t) => ({ id: t.id, nome: t.nome, slug: t.slug })));

      if (tid) {
        const [o, p, c] = await Promise.all([
          api(`/tenants/${tid}/orders`),
          api(`/tenants/${tid}/products`),
          api(`/tenants/${tid}/customers`),
        ]);
        setOrdersAll((o.orders || []).map((row) => ({ ...row, tenant_id: tid })));
        setProducts(p.products || []);
        setCustomers(c.customers || []);
        return;
      }

      const slice = tenants.slice(0, MAX_CONSOLIDATED_TENANTS);
      const bundles = await Promise.all(
        slice.map(async (t) => {
          const id = Number(t.id);
          try {
            const [o, p, c] = await Promise.all([
              api(`/tenants/${id}/orders`),
              api(`/tenants/${id}/products`),
              api(`/tenants/${id}/customers`),
            ]);
            const nom = String(t.nome || t.slug || `Empresa ${id}`);
            const rows = (o.orders || []).map((row) => ({
              ...row,
              tenant_id: id,
              tenant_nome: nom,
            }));
            return { orders: rows, products: p.products || [], customers: c.customers || [] };
          } catch {
            return { orders: [], products: [], customers: [] };
          }
        })
      );
      setOrdersAll(bundles.flatMap((b) => b.orders));
      setProducts(bundles.flatMap((b) => b.products));
      setCustomers(bundles.flatMap((b) => b.customers));
    } catch (e) {
      setError(e?.message || 'Não foi possível carregar o painel.');
      setOrdersAll([]);
      setProducts([]);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, setTenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredOrders = useMemo(() => {
    if (!range) return [];
    return ordersAll.filter((o) => isTimestampInRange(o.created_at, range.from, range.to));
  }, [ordersAll, range]);

  const activeOrders = useMemo(
    () => filteredOrders.filter((o) => o.status !== 'cancelled'),
    [filteredOrders]
  );

  const revenue = useMemo(
    () => activeOrders.reduce((s, o) => s + Number(o.total || 0), 0),
    [activeOrders]
  );

  const statusCounts = useMemo(() => {
    const c = { pending: 0, paid: 0, sent_to_erp: 0, cancelled: 0 };
    for (const o of filteredOrders) {
      const k = o.status;
      if (k in c) c[k] += 1;
    }
    return c;
  }, [filteredOrders]);

  const ticketMedio = activeOrders.length ? revenue / activeOrders.length : 0;

  const taxaConclusao = useMemo(() => {
    const denom = filteredOrders.filter((o) => o.status !== 'cancelled').length;
    if (!denom) return null;
    const done = filteredOrders.filter((o) => o.status === 'paid' || o.status === 'sent_to_erp').length;
    return Math.round((done / denom) * 1000) / 10;
  }, [filteredOrders]);

  const newCustomersInRange = useMemo(() => {
    if (!range) return 0;
    return customers.filter((c) => isTimestampInRange(c.created_at, range.from, range.to)).length;
  }, [customers, range]);

  const newProductsInRange = useMemo(() => {
    if (!range) return 0;
    return products.filter((p) => isTimestampInRange(p.created_at, range.from, range.to)).length;
  }, [products, range]);

  const byDay = useMemo(() => {
    const map = {};
    if (!range) return map;
    for (const o of activeOrders) {
      const d = new Date(String(o.created_at).replace(' ', 'T'));
      if (Number.isNaN(d.getTime())) continue;
      const key = toLocalYmd(d);
      if (!map[key]) map[key] = { count: 0, revenue: 0 };
      map[key].count += 1;
      map[key].revenue += Number(o.total || 0);
    }
    return map;
  }, [activeOrders, range]);

  const chartDays = useMemo(() => (range ? eachLocalDayInRange(range.from, range.to) : []), [range]);

  const recent = useMemo(() => {
    return [...filteredOrders]
      .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))
      .slice(0, 10);
  }, [filteredOrders]);

  const currentTenantName = useMemo(() => {
    if (!tenantId) return null;
    const t = tenantList.find((x) => Number(x.id) === Number(tenantId));
    return t?.nome || `Empresa #${tenantId}`;
  }, [tenantId, tenantList]);

  const consolidatedNote =
    !tenantId && tenantList.length > MAX_CONSOLIDATED_TENANTS
      ? `Agregámos no máximo ${MAX_CONSOLIDATED_TENANTS} empresas por desempenho. Use uma empresa selecionada para números completos.`
      : !tenantId && tenantList.length > 1
        ? 'Visão agregada de todas as suas empresas (pedidos com etiqueta por loja).'
        : null;

  return (
    <div className="dashboard-root pb-16 pt-6">
      <style>{`
        .dashboard-root { --dash-stagger: 55ms; }
        @keyframes dashReveal {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .dash-reveal > * {
          animation: dashReveal 0.55s cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        .dash-reveal > *:nth-child(1) { animation-delay: calc(var(--dash-stagger) * 0); }
        .dash-reveal > *:nth-child(2) { animation-delay: calc(var(--dash-stagger) * 1); }
        .dash-reveal > *:nth-child(3) { animation-delay: calc(var(--dash-stagger) * 2); }
        .dash-reveal > *:nth-child(4) { animation-delay: calc(var(--dash-stagger) * 3); }
        .dash-reveal > *:nth-child(5) { animation-delay: calc(var(--dash-stagger) * 4); }
        .dash-reveal > *:nth-child(6) { animation-delay: calc(var(--dash-stagger) * 5); }
      `}</style>

      {/* Textura muito subtil — profundidade sem competir com o AnimatedBackground global */}
      <div
        className="pointer-events-none fixed inset-0 z-[1] opacity-[0.035] dark:opacity-[0.06]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative z-[2] space-y-8">
        <header className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.35em] text-accent dark:text-emerald-300/90">
              Painel
            </p>
            <h1 className="mt-2 font-display text-4xl font-semibold tracking-tight text-ink dark:text-frost md:text-5xl">
              Dashboard
            </h1>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-ink/65 dark:text-frost-muted">
              {user?.name ? (
                <>
                  Olá, <span className="font-semibold text-ink dark:text-frost">{user.name}</span> — aqui vê o pulso da
                  operação: pedidos, receita e ritmo diário. Ajuste o período para reuniões, metas ou conciliação com o
                  ERP.
                </>
              ) : (
                <>
                  Resumo operacional: pedidos, receita e ritmo diário. Ajuste o período para reuniões, metas ou
                  conciliação com o ERP.
                </>
              )}
            </p>
            {currentTenantName ? (
              <p className="mt-3 inline-flex items-center gap-2 rounded-full border border-accent/25 bg-accent/10 px-3 py-1 text-xs font-semibold text-ink dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-100">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />
                {currentTenantName}
              </p>
            ) : (
              <p className="mt-3 text-xs font-medium text-ink/50 dark:text-frost-muted">
                Nenhuma empresa selecionada — métricas somam todas as lojas a que tem acesso.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-3 sm:items-end">
            <p className="text-right text-xs font-semibold text-ink/50 dark:text-frost-muted">{rangeLabel}</p>
            <div className="flex flex-wrap justify-end gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => applyPreset(/** @type {'7' | '15' | '30' | 'custom'} */ (p.id))}
                  className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wide transition ${
                    preset === p.id
                      ? 'bg-ink text-mist shadow-lifted dark:bg-frost dark:text-ink'
                      : 'border border-ink/10 bg-white/60 text-ink/70 hover:border-accent/40 dark:border-white/10 dark:bg-white/5 dark:text-frost'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {preset === 'custom' ? (
              <div className="flex flex-wrap items-center justify-end gap-2 rounded-2xl border border-ink/10 bg-white/50 px-3 py-2 dark:border-white/10 dark:bg-night-surface/80">
                <label className="flex items-center gap-2 text-xs font-semibold text-ink/60 dark:text-frost-muted">
                  De
                  <input
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    className="rounded-lg border border-ink/15 bg-white px-2 py-1 text-xs dark:border-white/10 dark:bg-night-elevated dark:text-mist"
                  />
                </label>
                <label className="flex items-center gap-2 text-xs font-semibold text-ink/60 dark:text-frost-muted">
                  Até
                  <input
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    className="rounded-lg border border-ink/15 bg-white px-2 py-1 text-xs dark:border-white/10 dark:bg-night-elevated dark:text-mist"
                  />
                </label>
                {!range && customFrom && customTo ? (
                  <span className="text-[11px] font-medium text-accent2">Datas inválidas</span>
                ) : null}
              </div>
            ) : null}
          </div>
        </header>

        {consolidatedNote ? (
          <div className="rounded-2xl border border-amber-400/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-950 dark:border-amber-300/25 dark:bg-amber-500/10 dark:text-amber-50">
            {consolidatedNote}
          </div>
        ) : null}

        {error ? <p className="text-sm font-semibold text-accent2">{error}</p> : null}

        {loading ? (
          <div className="flex min-h-[28vh] flex-col items-center justify-center gap-4 rounded-3xl border border-ink/10 bg-white/40 dark:border-white/10 dark:bg-night-surface/50">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-accent/30 border-t-accent" />
            <p className="text-sm font-medium text-ink/55 dark:text-frost-muted">A carregar indicadores…</p>
          </div>
        ) : !range ? (
          <p className="text-sm text-ink/60 dark:text-frost-muted">Defina um intervalo de datas válido para ver o painel.</p>
        ) : (
          <>
            <section className="dash-reveal grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <KpiTile
                title="Pedidos no período"
                value={String(filteredOrders.length)}
                sub={`${activeOrders.length} sem cancelamento`}
                hint="Inclui todos os estados; cancelados entram na contagem total."
              />
              <KpiTile
                title="Receita (estimada)"
                value={formatBrl(revenue)}
                sub="Soma dos totais, exceto cancelados"
                accent="accent2"
              />
              <KpiTile
                title="Ticket médio"
                value={activeOrders.length ? formatBrl(ticketMedio) : '—'}
                sub={activeOrders.length ? 'Por pedido não cancelado' : 'Sem pedidos ativos no período'}
              />
              <KpiTile
                title="Pedidos concluídos"
                value={taxaConclusao != null ? `${taxaConclusao}%` : '—'}
                sub="Faturado ou enviado ao sistema / pedidos não cancelados"
                hint="Indicador interno de avanço no fluxo operacional."
              />
            </section>

            <section className="dash-reveal grid gap-4 md:grid-cols-3">
              <div className="rounded-2xl border border-ink/10 bg-white/70 p-5 dark:border-white/10 dark:bg-night-surface/85">
                <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-ink/45 dark:text-frost-muted">
                  Catálogo
                </p>
                <p className="mt-2 font-display text-3xl font-semibold text-ink dark:text-frost">{products.length}</p>
                <p className="text-xs text-ink/55 dark:text-frost-muted">produtos listados na API</p>
                <p className="mt-2 text-[11px] text-ink/45 dark:text-frost-muted">
                  +{newProductsInRange} criados no período filtrado
                </p>
              </div>
              <div className="rounded-2xl border border-ink/10 bg-white/70 p-5 dark:border-white/10 dark:bg-night-surface/85">
                <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-ink/45 dark:text-frost-muted">
                  Clientes
                </p>
                <p className="mt-2 font-display text-3xl font-semibold text-ink dark:text-frost">{customers.length}</p>
                <p className="text-xs text-ink/55 dark:text-frost-muted">registos na base</p>
                <p className="mt-2 text-[11px] text-ink/45 dark:text-frost-muted">
                  +{newCustomersInRange} novos no período filtrado
                </p>
              </div>
              <div className="rounded-2xl border border-ink/10 bg-gradient-to-br from-accent/15 via-white/80 to-white/80 p-5 dark:border-emerald-500/20 dark:from-emerald-500/10 dark:via-night-surface dark:to-night-deep">
                <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-ink/45 dark:text-emerald-200/80">
                  Atalhos
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link
                    to="/app/pedidos"
                    className="rounded-full bg-ink px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-mist dark:bg-frost dark:text-ink"
                  >
                    Pedidos
                  </Link>
                  <Link
                    to="/app/produtos"
                    className="rounded-full border border-ink/15 px-3 py-1.5 text-xs font-bold uppercase tracking-wide dark:border-white/15"
                  >
                    Produtos
                  </Link>
                  <Link
                    to="/app/clientes"
                    className="rounded-full border border-ink/15 px-3 py-1.5 text-xs font-bold uppercase tracking-wide dark:border-white/15"
                  >
                    Clientes
                  </Link>
                  <Link
                    to="/app/empresa"
                    className="rounded-full border border-accent/40 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-accent dark:text-emerald-200"
                  >
                    Empresas
                  </Link>
                </div>
              </div>
            </section>

            <section className="dash-reveal grid gap-6 lg:grid-cols-2">
              <div className="rounded-3xl border border-ink/10 bg-white/75 p-6 shadow-md backdrop-blur dark:border-white/[0.08] dark:bg-night-surface/90 dark:shadow-night-card">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="font-display text-xl font-semibold text-ink dark:text-frost">Receita por dia</h2>
                    <p className="text-xs text-ink/50 dark:text-frost-muted">Barras proporcionais ao maior dia do intervalo</p>
                  </div>
                </div>
                <div className="mt-6">
                  {chartDays.length > 60 ? (
                    <p className="text-sm text-ink/55 dark:text-frost-muted">Intervalo longo demais para o gráfico diário; use 30 dias ou menos.</p>
                  ) : chartDays.length ? (
                    <DailyBars days={chartDays} byDay={byDay} />
                  ) : (
                    <p className="text-sm text-ink/50">Sem dados.</p>
                  )}
                </div>
              </div>

              <div className="rounded-3xl border border-ink/10 bg-white/75 p-6 shadow-md backdrop-blur dark:border-white/[0.08] dark:bg-night-surface/90 dark:shadow-night-card">
                <h2 className="font-display text-xl font-semibold text-ink dark:text-frost">Estado dos pedidos</h2>
                <p className="text-xs text-ink/50 dark:text-frost-muted">Distribuição no período seleccionado</p>
                <div className="mt-8">
                  <StatusDonut counts={statusCounts} />
                </div>
              </div>
            </section>

            <section className="dash-reveal rounded-3xl border border-ink/10 bg-white/75 p-6 shadow-md backdrop-blur dark:border-white/[0.08] dark:bg-night-surface/90 dark:shadow-night-card">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <h2 className="font-display text-xl font-semibold text-ink dark:text-frost">Pedidos recentes</h2>
                  <p className="text-xs text-ink/50 dark:text-frost-muted">Ordenados pela data de criação no período</p>
                </div>
                <Link to="/app/pedidos" className="text-xs font-bold uppercase tracking-wide text-accent hover:underline">
                  Ver todos →
                </Link>
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full min-w-[520px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-ink/10 text-[10px] font-bold uppercase tracking-wider text-ink/45 dark:border-white/10 dark:text-frost-muted">
                      {!tenantId ? <th className="pb-3 pr-4">Empresa</th> : null}
                      <th className="pb-3 pr-4">#</th>
                      <th className="pb-3 pr-4">Cliente</th>
                      <th className="pb-3 pr-4">Estado</th>
                      <th className="pb-3 pr-4">Total</th>
                      <th className="pb-3">Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((o) => (
                      <tr
                        key={`${o.tenant_id ?? tenantId}-${o.id}`}
                        className="border-b border-ink/5 last:border-0 dark:border-white/5"
                      >
                        {!tenantId ? (
                          <td className="py-3 pr-4 text-xs font-medium text-ink/70 dark:text-frost-muted">
                            {o.tenant_nome ?? '—'}
                          </td>
                        ) : null}
                        <td className="py-3 pr-4 font-mono text-xs text-ink/60 dark:text-frost-muted">{o.id}</td>
                        <td className="py-3 pr-4 font-medium">{o.customer_nome}</td>
                        <td className="py-3 pr-4">
                          <span className="rounded-full bg-ink/5 px-2 py-0.5 text-xs dark:bg-white/10">
                            {orderStatusLabelPtBr(o.status)}
                          </span>
                        </td>
                        <td className="py-3 pr-4 tabular-nums">{formatBrl(o.total || 0)}</td>
                        <td className="py-3 text-xs text-ink/55 dark:text-frost-muted">
                          {o.created_at
                            ? new Date(String(o.created_at).replace(' ', 'T')).toLocaleString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!recent.length ? (
                  <p className="mt-4 text-sm text-ink/50 dark:text-frost-muted">Nenhum pedido neste intervalo.</p>
                ) : null}
              </div>
            </section>

            {!tenantId && tenantList.length ? (
              <section className="dash-reveal rounded-3xl border border-ink/10 bg-white/60 p-6 dark:border-white/10 dark:bg-night-elevated/80">
                <h2 className="font-display text-lg font-semibold text-ink dark:text-frost">Escolher empresa para detalhe</h2>
                <p className="mt-1 text-xs text-ink/55 dark:text-frost-muted">
                  Um clique define a loja activa em todo o painel (produtos, pedidos, clientes).
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {tenantList.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTenantId(t.id)}
                      className={`rounded-2xl border px-4 py-2 text-left text-sm font-semibold transition ${
                        Number(tenantId) === Number(t.id)
                          ? 'border-accent bg-accent/15 text-ink dark:text-emerald-100'
                          : 'border-ink/10 bg-white/80 hover:border-accent/30 dark:border-white/10 dark:bg-night-surface dark:hover:border-emerald-400/30'
                      }`}
                    >
                      {t.nome}
                      <span className="mt-0.5 block text-[10px] font-normal uppercase tracking-wider text-ink/45 dark:text-frost-muted">
                        /{t.slug}
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
